import { useState, useMemo, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { WelcomeOfferStatus } from "@/components/WelcomeOfferStatus"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { Dialog } from "@/components/Dialog/Dialog"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import {
  getAllUsersForExport,
  useUsers,
  type AdminUser,
  type UsersSort,
  type WelcomeOfferListFilter,
} from "@/hooks/useUsers"
import { useToast } from "@/components/Toast"
import { client, APIError } from "@/api"
import { downloadUsersCsv } from "@/lib/userExport"
import { formatSignupSource } from "@/lib/signup-source"
import {
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"
import { formatDistanceToNow } from "date-fns"

// DB timestamps come in two shapes: ISO ("...T...Z") and SQLite "YYYY-MM-DD HH:MM:SS"
// (no zone). Normalize the bare form to UTC so the relative time is correct.
function parseDbTimestamp(value: string): Date {
  if (value.includes("T") || value.endsWith("Z")) return new Date(value)
  return new Date(value.replace(" ", "T") + "Z")
}

// The directory links to the offer workflow; all review actions live on the request page.
function WelcomeOfferCell({ user }: { user: AdminUser }) {
  if (!user.welcome_offer_stage) return null
  if (["shown", "dismissed"].includes(user.welcome_offer_stage)) return <WelcomeOfferStatus stage={user.welcome_offer_stage} />
  return <Link to={`/offers/diagrams/${user.public_id}`} onClick={(event) => event.stopPropagation()} className="hover:underline">
    <WelcomeOfferStatus stage={user.welcome_offer_stage} />
  </Link>
}

const WELCOME_OFFER_COLUMN: ColumnDef<AdminUser> = {
  accessorKey: "welcome_offer_stage",
  header: "Welcome offer",
  cell: ({ row }) => <WelcomeOfferCell user={row.original} />,
}

// Preserve existing bookmarked directory filters; the offer queue owns the visible workflow.
const WELCOME_OFFER_FILTER_CHIPS: { value: WelcomeOfferListFilter; label: string }[] = [
  { value: "claimed", label: "All claimed" },
  { value: "accepted", label: "Accepted" },
  { value: "details_received", label: "Details received" },
  { value: "emailed", label: "Emailed" },
  { value: "onboarded", label: "Onboarded" },
  { value: "cold", label: "Cold" },
]

export function Users() {
  const [page, setPage] = useState(0)
  const [pageSize] = useState(50)
  const [sort, setSort] = useState<UsersSort>("created_at")
  const [searchParams, setSearchParams] = useSearchParams()
  const offerParam = searchParams.get("offer")
  const welcomeOfferFilter = WELCOME_OFFER_FILTER_CHIPS.find((chip) => chip.value === offerParam)
    ?.value
  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [createForm, setCreateForm] = useState({ name: "", email: "" })
  const [isExporting, setIsExporting] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const { data, isLoading, error, refetch } = useUsers({
    limit: pageSize,
    offset: page * pageSize,
    search: debouncedSearch,
    sort,
    welcomeOffer: welcomeOfferFilter,
  })

  const handleUserClick = (userId: string) => {
    window.location.href = `/users/${userId}`
  }

  const handleExportUsers = async () => {
    setIsExporting(true)

    try {
      const users = await getAllUsersForExport(sort)
      downloadUsersCsv(users)
      toast({
        title: "Users exported",
        description: `Downloaded ${users.length} user${users.length === 1 ? "" : "s"}.`,
      })
    } catch (error) {
      toast({
        title: "Export failed",
        description:
          error instanceof APIError
            ? error.originalError.message
            : "The user export could not be created. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleCreateUser = async () => {
    try {
      const response = await (client.admin.users as any).$post({
        json: { name: createForm.name, email: createForm.email },
      })

      if (!response.ok) {
        const error = (await response.json()) as any
        throw new APIError(
          response.status,
          error.code || "CREATE_USER_FAILED",
          new Error(error.message || "Failed to create user")
        )
      }

      const result = await response.json()
      toast({
        title: result.created ? "User created" : "User already exists",
        description: result.created
          ? `Created ${createForm.name} (${createForm.email})`
          : `A user with email ${createForm.email} already exists.`,
      })
      setShowCreateDialog(false)
      setCreateForm({ name: "", email: "" })
      refetch()
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof APIError
            ? error.originalError.message
            : "Failed to create user. Please try again.",
        variant: "destructive",
      })
    }
  }

  // The backend already orders by the selected `sort` (created_at / last_seen), so the
  // page is rendered in server order, no client-side re-sort (which would only reorder
  // the current page and fight the server ordering).
  const sortedUsers = data?.users ?? []

  const columns = useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="cursor-pointer">
            <p className="font-medium text-primary hover:underline underline-offset-2" data-testid={`user-name-${row.original.public_id}`}>
              {row.original.name}
            </p>
            <p className="text-sm text-secondary">{row.original.email}</p>
          </div>
        ),
      },
      {
        accessorKey: "email_verified",
        header: "Status",
        cell: ({ row }) => (
          <div className="flex items-center">
            {row.original.email_verified ? (
              <>
                <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm">Verified</span>
              </>
            ) : (
              <>
                <XCircleIcon className="h-4 w-4 text-yellow-600 mr-2" />
                <span className="text-sm">Unverified</span>
              </>
            )}
          </div>
        ),
      },
      {
        accessorKey: "is_superuser",
        header: "Access",
        cell: ({ row }) => (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            row.original.is_superuser
              ? "bg-primary/10 text-primary"
              : "bg-secondary/10 text-secondary"
          }`}>
            {row.original.is_superuser ? "Admin" : "User"}
          </span>
        ),
      },
      {
        id: "team",
        header: "Team",
        cell: ({ row }) => {
          const teams = row.original.teams ?? []
          if (teams.length === 0) {
            return <span className="text-sm text-secondary">None</span>
          }
          const [first, ...rest] = teams
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-primary">{first.name}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary capitalize">
                {first.role}
              </span>
              {rest.length > 0 && (
                <span
                  className="text-xs text-secondary"
                  title={rest.map((t) => `${t.name} (${t.role})`).join(", ")}
                >
                  +{rest.length}
                </span>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: "diagram_count",
        header: "Diagrams",
        cell: ({ row }) => {
          const count = row.original.diagram_count ?? 0
          if (count === 0) {
            return <span className="text-sm text-secondary tabular-nums">0</span>
          }
          return (
            <Link
              to={`/diagrams?creator=${encodeURIComponent(row.original.public_id)}&creatorName=${encodeURIComponent(row.original.name)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-primary tabular-nums hover:underline"
            >
              {count}
            </Link>
          )
        },
      },
      WELCOME_OFFER_COLUMN,
      {
        accessorKey: "last_seen_at",
        header: "Last seen",
        cell: ({ row }) => {
          const lastSeen = row.original.last_seen_at
          if (!lastSeen) {
            return <span className="text-sm text-secondary">Never</span>
          }
          const date = parseDbTimestamp(lastSeen)
          return (
            <span className="text-sm text-secondary" title={date.toLocaleString()}>
              {formatDistanceToNow(date, { addSuffix: true })}
            </span>
          )
        },
      },
      {
        accessorKey: "created_at",
        header: "Joined",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: "signup_source",
        header: "Source",
        cell: ({ row }) => {
          const { surface, landing, from, utm, ad, summary, isEmpty } = formatSignupSource(row.original)
          if (isEmpty) {
            return <span className="text-sm text-secondary">—</span>
          }
          // One part per line so the column stays narrow: the table is already tight, and list
          // rows are two lines tall anyway. The full text is in the tooltip.
          const lines = [surface, landing, from, utm, ad ? "ad click" : undefined].filter(Boolean)
          return (
            <div className="max-w-[9rem] text-xs leading-snug text-secondary" title={summary}>
              {lines.map((line) => (
                <span key={line} className="block truncate">
                  {line}
                </span>
              ))}
            </div>
          )
        },
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Users</h1>
            <p className="text-secondary">Manage user accounts and permissions</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowExportDialog(true)}
              disabled={isExporting}
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              {isExporting ? "Exporting…" : "Export"}
            </Button>
            <Button onClick={() => setShowCreateDialog(true)}>
              <UserPlusIcon className="h-4 w-4" />
              Create User
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Input
            placeholder="Search users by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="max-w-md"
          />
          {debouncedSearch && (
            <Button variant="outline" onClick={() => setSearchInput("")}>
              Clear
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-secondary">Sort by</span>
            <Button
              variant={sort === "created_at" ? "default" : "outline"}
              onClick={() => {
                setSort("created_at")
                setPage(0)
              }}
            >
              Recently joined
            </Button>
            <Button
              variant={sort === "last_seen" ? "default" : "outline"}
              onClick={() => {
                setSort("last_seen")
                setPage(0)
              }}
            >
              Last active
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <Link to="/offers/diagrams" className="font-medium text-blue-600 hover:underline">Welcome offer requests →</Link>
          {welcomeOfferFilter && <Button variant="ghost" size="sm" onClick={() => { setSearchParams({}); setPage(0) }}>Clear offer filter</Button>}
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : data?.users?.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="h-12 w-12" />}
            title={debouncedSearch ? "No matches" : "No users found"}
            description={
              debouncedSearch
                ? `No users match "${debouncedSearch}".`
                : "No users have been created yet."
            }
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={sortedUsers}
              showToolbar={false}
              showPagination={false}
              onRowClick={(user) => handleUserClick(user.public_id)}
            />
            <div className="flex items-center justify-between text-sm text-secondary">
              <span>
                Showing {sortedUsers.length} result{sortedUsers.length === 1 ? "" : "s"}
                {page > 0 && ` (page ${page + 1})`}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={sortedUsers.length < pageSize}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        title="Export all users?"
        description="This downloads every user's name, email, account status, team memberships, activity, and signup date. Store and share the CSV securely."
        confirmLabel="Export all users"
        onConfirm={handleExportUsers}
      />

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>
              Create a new user account. They can sign in via Google or use "Forgot Password" to set a password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={!createForm.name || !createForm.email}
              >
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
